// src/hooks/useFactoryDetailsMaster.js
import { useEffect, useState } from "react";
import CommonService from "../services/common.service";
import factoryDetails from "../data/FactoryDetails.json";

export function useFactoryDetailsMaster() {
  const [factorySizeUnits, setFactorySizeUnits] = useState([]);
  const [factoryDistanceUnits, setFactoryDistanceUnits] = useState([]);
  const [shippingTerms, setShippingTerms] = useState([]);
  const [modeOfTransport, setModeOfTransport] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [latePaymentPolicy, setLatePaymentPolicy] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await CommonService.get("/master/factory-ShippingTerms");
        
        // Extract data from response
        if (response && response.success && response.data) {
          const apiShippingTerms = response.data.shippingTerms || response.data.master_shipping_terms || [];
          const apiModeOfTransport = response.data.modeOfTransport || response.data.master_mode_of_transport || [];
          const apiPaymentMethod = response.data.paymentMethod || response.data.master_payment_method || [];
          const apiPaymentSchedule = response.data.paymentSchedule || response.data.master_payment_schedule || [];
          const apiLatePaymentPolicy = response.data.latePaymentPolicy || response.data.master_late_payment_policy || [];
          
          setShippingTerms(apiShippingTerms);
          setModeOfTransport(apiModeOfTransport);
          setPaymentMethod(apiPaymentMethod);
          setPaymentSchedule(apiPaymentSchedule);
          setLatePaymentPolicy(apiLatePaymentPolicy);
        }
        
        // Keep factory size and distance units from JSON for now (or fetch from API if available)
        setFactorySizeUnits(factoryDetails.factorySizeUnits || []);
        setFactoryDistanceUnits(factoryDetails.factoryDistanceUnits || []);
      } catch (error) {
        console.error("Error fetching factory details from API:", error);
        // Fallback to JSON if API fails
        setFactorySizeUnits(factoryDetails.factorySizeUnits || []);
        setFactoryDistanceUnits(factoryDetails.factoryDistanceUnits || []);
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, []);

  // Map to react-select format { value, label }
  const factorySizeUnitsOptions = factorySizeUnits.map((unit) => ({
    value: unit.id || unit.value,
    label: unit.label || unit.name
  })).filter(opt => opt.value !== undefined && opt.value !== null && opt.label);

  const factoryDistanceUnitsOptions = factoryDistanceUnits.map((unit) => ({
    value: unit.id || unit.value,
    label: unit.label || unit.name
  })).filter(opt => opt.value !== undefined && opt.value !== null && opt.label);

  // Map shipping terms to react-select format
  // API format: {incoterm_id: 1, incoterm: 'FOB Destination'}
  const shippingTermsOptions = shippingTerms.map((st, index) => {
    const value = st.incoterm_id || st.shipping_term_id || st.id || st.value;
    const label = st.incoterm || st.shipping_term || st.shipping_term_name || st.label || st.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map mode of transport to react-select format
  // API format: {shipping_info_id: 1, shipping_info: 'Sea freight'}
  const modeOfTransportOptions = modeOfTransport.map((mot, index) => {
    const value = mot.shipping_info_id || mot.transport_mode_id || mot.id || mot.value;
    const label = mot.shipping_info || mot.transport_mode || mot.mode_name || mot.label || mot.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map payment method to react-select format
  const paymentMethodOptions = paymentMethod.map((pm) => {
    const value = pm.payment_method_id || pm.id || pm.value;
    const label = pm.payment_method || pm.method_name || pm.label || pm.name;
    return { value, label };
  }).filter(opt => opt.value !== undefined && opt.value !== null && opt.label);

  // Map payment schedule to react-select format
  const paymentScheduleOptions = paymentSchedule.map((ps) => {
    const value = ps.payment_schedule_id || ps.id || ps.value;
    const label = ps.payment_schedule || ps.schedule_name || ps.label || ps.name;
    return { value, label };
  }).filter(opt => opt.value !== undefined && opt.value !== null && opt.label);

  // Map late payment policy to react-select format
  const latePaymentPolicyOptions = latePaymentPolicy.map((lpp) => {
    const value = lpp.late_payment_policy_id || lpp.id || lpp.value;
    const label = lpp.late_payment_policy || lpp.policy_name || lpp.label || lpp.name;
    return { value, label };
  }).filter(opt => opt.value !== undefined && opt.value !== null && opt.label);

  return {
    factorySizeUnits,
    factorySizeUnitsOptions,
    factoryDistanceUnits,
    factoryDistanceUnitsOptions,
    shippingTerms,
    shippingTermsOptions,
    modeOfTransport,
    modeOfTransportOptions,
    paymentMethod,
    paymentMethodOptions,
    paymentSchedule,
    paymentScheduleOptions,
    latePaymentPolicy,
    latePaymentPolicyOptions,
    loading
  };
}

